<template>
  <div
    class="testimonials-carousel-wrapper"
    :class="`testimonials-carousel-wrapper--${currentItemsPerSlide}-up`"
  >
    <div
      class="testimonials-carousel"
      role="region"
      aria-roledescription="carousel"
      aria-label="Testimonials"
      @mouseenter="stopAutoPlay"
      @mouseleave="startAutoPlay"
      @keydown="onCarouselKeydown"
      tabindex="0"
    >
      <button
        v-if="maxSlideIndex > 0"
        class="carousel-arrow carousel-arrow--prev"
        @click="prevTestimonial"
        aria-label="Previous testimonial"
        :disabled="isTransitioning"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M15 19L8 12L15 5"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <div class="carousel-viewport" :style="viewportStyle">
        <div
          class="carousel-track"
          :style="trackStyle"
          @touchstart.passive="onTouchStart"
          @touchend.passive="onTouchEnd"
          aria-live="polite"
        >
          <div
            v-for="(item, index) in testimonials"
            :ref="(el) => setSlideRef(el, index)"
            :key="`${item.author}-${index}`"
            class="carousel-slide"
            role="group"
            aria-roledescription="slide"
            :aria-label="`Testimonial ${index + 1} of ${testimonials.length}`"
            :aria-hidden="
              index < activeTestimonialIndex ||
              index >= activeTestimonialIndex + currentItemsPerSlide
            "
          >
            <article class="testimonial-card">
              <blockquote
                :ref="(el) => setQuoteContainerRef(el, index)"
                class="testimonial-quote"
              >
                <p
                  :ref="(el) => setQuoteRef(el, index)"
                  class="testimonial-quote__text"
                >
                  <template v-if="truncatedFlags[index]">
                    {{ truncatedTexts[index] }}...<span
                      class="testimonial-quote__view-more"
                      role="button"
                      tabindex="0"
                      @click="openTestimonialModal(item)"
                      @keydown.enter.prevent="openTestimonialModal(item)"
                      @keydown.space.prevent="openTestimonialModal(item)"
                      >View More</span
                    >
                  </template>
                  <template v-else>
                    {{ item.quote }}
                  </template>
                </p>
              </blockquote>

              <div class="testimonial-card__divider" aria-hidden="true"></div>

              <footer class="testimonial-author">
                <div class="testimonial-author__avatar-wrap">
                  <img
                    src="/images/maker-cart/get-started/sparkle.svg"
                    alt=""
                    class="testimonial-author__sparkle"
                    aria-hidden="true"
                  />
                  <div
                    class="testimonial-author__avatar"
                    :class="{
                      'testimonial-author__avatar--logo':
                        item.imageType === 'logo',
                      'testimonial-author__avatar--initials': !item.image,
                    }"
                  >
                    <img
                      v-if="item.image"
                      :src="item.image"
                      :alt="item.author"
                      class="testimonial-author__photo"
                    />
                    <span v-else class="testimonial-author__initials">
                      {{ getInitials(item.author) }}
                    </span>
                  </div>
                </div>
                <div class="testimonial-author__info">
                  <cite class="testimonial-author__name">{{
                    item.author
                  }}</cite>
                  <p class="testimonial-author__title">{{ item.title }}</p>
                </div>
              </footer>
            </article>
          </div>
        </div>
      </div>

      <button
        v-if="maxSlideIndex > 0"
        class="carousel-arrow carousel-arrow--next"
        @click="nextTestimonial"
        aria-label="Next testimonial"
        :disabled="isTransitioning"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M9 5L16 12L9 19"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>

    <div
      class="carousel-dots"
      role="tablist"
      aria-label="Testimonial slides"
      v-if="dotSlides.length > 1"
    >
      <button
        v-for="(item, index) in dotSlides"
        :key="index"
        class="carousel-dot"
        :class="{ active: index === activeTestimonialIndex }"
        role="tab"
        :aria-selected="index === activeTestimonialIndex"
        :aria-label="`Go to testimonial by ${item.author}`"
        @click="goToTestimonial(index)"
      ></button>
    </div>

    <v-dialog
      v-model="isModalOpen"
      :max-width="modalMaxWidth"
      content-class="testimonial-modal-dialog"
      transition="dialog-transition"
      @after-leave="selectedTestimonial = null"
    >
      <div
        v-if="selectedTestimonial"
        class="testimonial-modal"
        role="dialog"
        aria-modal="true"
        :aria-label="`Testimonial from ${selectedTestimonial.author}`"
      >
        <div class="testimonial-modal__header">
          <button
            type="button"
            class="testimonial-modal__close"
            aria-label="Close testimonial"
            @click="closeTestimonialModal"
          >
            <img
              src="/images/maker-cart/get-started/testimonial-modal-close.svg"
              alt=""
              width="40"
              height="40"
            />
          </button>
        </div>

        <div class="testimonial-modal__body">
          <img
            src="/images/maker-cart/get-started/sparkle.svg"
            alt=""
            class="testimonial-modal__sparkle"
            aria-hidden="true"
          />
          <p class="testimonial-modal__quote">
            {{ selectedTestimonial.quote }}
          </p>

          <div class="testimonial-modal__divider" aria-hidden="true"></div>

          <footer class="testimonial-author testimonial-author--modal">
            <div class="testimonial-author__avatar-wrap">
              <img
                src="/images/maker-cart/get-started/sparkle.svg"
                alt=""
                class="testimonial-author__sparkle"
                aria-hidden="true"
              />
              <div
                class="testimonial-author__avatar"
                :class="{
                  'testimonial-author__avatar--logo':
                    selectedTestimonial.imageType === 'logo',
                  'testimonial-author__avatar--initials':
                    !selectedTestimonial.image,
                }"
              >
                <img
                  v-if="selectedTestimonial.image"
                  :src="selectedTestimonial.image"
                  :alt="selectedTestimonial.author"
                  class="testimonial-author__photo"
                />
                <span v-else class="testimonial-author__initials">
                  {{ getInitials(selectedTestimonial.author) }}
                </span>
              </div>
            </div>
            <div class="testimonial-author__info">
              <cite class="testimonial-author__name">{{
                selectedTestimonial.author
              }}</cite>
              <p class="testimonial-author__title">
                {{ selectedTestimonial.title }}
              </p>
            </div>
          </footer>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  defineProps,
  nextTick,
} from "vue";

const props = defineProps({
  itemsPerSlide: {
    type: Number,
    default: 2,
  },
});

const testimonials = [
  // Singapore
  {
    quote:
      "The expertise and experience provided by edm8ker have been invaluable to the success of our school's maker journey. They have helped strengthen our organization and build its capacity. Their philosophy of growing the maker community is truly noble.",
    author: "Teo Siew Khim",
    title: "Vice Principal, Fuhua Primary School",
    image: "/images/maker-cart/get-started/Fuhua-Primary-School.jpeg",
    imageType: "logo",
    region: "Singapore",
  },
  {
    quote:
      "We've worked with edm8ker on several projects, and they consistently ensure that the workshops and activities meet a high standard. They are a wonderful, dynamic team, able to cater to both the students' and school's needs.",
    author: "Nah Hong Leong",
    title: "HOD (RID), Commonwealth Secondary School",
    image: "/images/maker-cart/get-started/Coomonwealth-Secondary-School.jpeg",
    imageType: "logo",
    region: "Singapore",
  },
  {
    quote:
      "We appreciate working with people who share our mindset, principles, and expected outcomes about making. Edm8ker excels in customizing programs and consistently goes the extra mile to support our school's journey.",
    author: "Jennifer Long",
    title: "HOD Science, Westwood Primary School",
    image: "/images/maker-cart/get-started/Westwood-Primary-School.png",
    imageType: "logo",
    region: "Singapore",
  },
  {
    quote:
      "The activities in the Makercart have allowed us to seamlessly incorporate Maker Education into subjects like social studies, science, art, and even music lessons, providing students with authentic learning experiences.",
    author: "Mam Maha Letchimi",
    title: "HOD Special Projects, Bendemeer Primary School",
    region: "Singapore",
    image: "/images/maker-cart/get-started/Bendemeer-Primary-Final.png",
  },
  // United States
  {
    quote:
      "The Makercart provides engaging, inquiry-based design and making experiences that have transformed our approach to teaching.",
    author: "Victor Pereira",
    title: "Faculty Co-Chair, Harvard Graduate School of Education",
    region: "United States",
    image:
      "/images/maker-cart/get-started/Harvard-Graduate-School-of-Education.jpg",
    imageType: "logo",
  },
  {
    quote:
      "Working with edm8ker has been a game-changer for our after-school program. What started as a pilot has blossomed into a partnership that has truly enriched both our staff and students. The coaching and support from edm8ker gave our team the skills and confidence to deliver engaging, tech-driven lessons, while our students have found joy in exploring technology and proudly sharing their creations with their families.",
    author: "Matthew Caughey",
    title: "Executive Director, The Elizabeth Peabody House, Somerville, MA",
    region: "United States",
    image:
      "/images/maker-cart/get-started/The-Elizabeth-Peabody-House-Somerville-MA.jpg",
    imageType: "logo",
  },
  {
    quote:
      "My students are thrilled to present their projects on platforms like Brush Ninja and Mathigon. The enthusiasm and engagement have skyrocketed, turning every lesson into a memorable experience.",
    author: "Dr. Rehana Lynda Mohamed",
    title:
      "School Educational Consultant, St. Anthony's School of Higher Education, Miami, FL",
    region: "United States",
    image: "/images/maker-cart/get-started/Dr-Rehana-Mohamed.jpeg",
    imageType: "portrait",
  },
  {
    quote:
      "Our students are so captivated by the activities that they forget they're reinforcing classroom lessons. The excitement is contagious—they never want the session to end!",
    author: "Marcos & El",
    title: "The Elizabeth Peabody House, Somerville, MA",
    region: "United States",
    image:
      "/images/maker-cart/get-started/The-Elizabeth-Peabody-House-Somerville-MA.jpg",
    imageType: "logo",
  },
  {
    quote:
      "Makercrate turns STEM and academics into hands-on exploration — students design, build, and problem-solve fully engaged every step of the way.",
    author: "Wendie Turner",
    title:
      "Media Technology Specialist, Stanton-Smith Elementary, Whitehouse, TX",
    region: "United States",
    image: "/images/maker-cart/get-started/Wendie-Turner-Ambassador.png",
    imageType: "portrait",
  },
  {
    quote:
      "I used the edm8ker crate for the first time, and it was a really easy experience compared to other similar products. The embedded instructional videos were especially helpful as my students began creating their paper circuit cards. The kids were so excited to see their circuits work for the first time, and it was a great way to learn about electricity.",
    author: "Rachel Koskelin",
    title: "Science/Social Studies Teacher, Lenox Elementary School",
    region: "United States",
    image: "/images/maker-cart/get-started/Rachel-Koselin.jpg",
    imageType: "portrait",
  },
  // Japan
  {
    quote:
      "With a prepared set like Makercart, [prep] effort is reduced significantly, which makes it much easier to get started. Teachers can adapt it depending on their students, which makes it practical and flexible to use in real classrooms.",
    author: "Hayashi",
    title:
      "Elementary School Teacher, Elementary School Affiliated to Hyogo University of Teacher Education (Makercart Pilot School)",
    region: "Japan",
    image: "/images/maker-cart/get-started/Hyogo-University.png",
    imageType: "logo",
  },
  {
    quote:
      "During the activity, students were actively thinking while making. They tried different ideas, made mistakes, and adjusted their approach as they worked.",
    author: "Izutani",
    title:
      "Elementary School Teacher, Elementary School Affiliated to Hyogo University of Teacher Education (Makercart Pilot School)",
    region: "Japan",
    image: "/images/maker-cart/get-started/Hyogo-University.png",
    imageType: "logo",
  },
];

const getInitials = (author) => {
  const parts = author.replace(/&/g, " ").trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return author.slice(0, 2).toUpperCase();
};

const slideRefs = ref([]);
const quoteRefs = ref([]);
const quoteContainerRefs = ref([]);
const truncatedFlags = ref({});
const truncatedTexts = ref({});
const isModalOpen = ref(false);
const selectedTestimonial = ref(null);
let resizeObserver = null;

const QUOTE_HEIGHT_TOLERANCE = 2;
let truncationFrame = null;

const setSlideRef = (el, index) => {
  if (el) {
    slideRefs.value[index] = el;
  }
};

const setQuoteRef = (el, index) => {
  if (el) {
    quoteRefs.value[index] = el;
  }
};

const setQuoteContainerRef = (el, index) => {
  if (el) {
    quoteContainerRefs.value[index] = el;
  }
};

const windowWidth = ref(
  typeof window !== "undefined" ? window.innerWidth : 1200
);

const modalMaxWidth = computed(() => {
  return windowWidth.value < 768 ? 361 : 704;
});

const createQuoteMeasure = (el, width) => {
  const styles = window.getComputedStyle(el);
  const measure = document.createElement("p");
  measure.setAttribute("aria-hidden", "true");
  measure.style.cssText = [
    "position:absolute",
    "visibility:hidden",
    "pointer-events:none",
    "left:-9999px",
    "top:0",
    "margin:0",
    "padding:0",
    `width:${Math.floor(width)}px`,
    `font-family:${styles.fontFamily}`,
    `font-size:${styles.fontSize}`,
    `font-weight:${styles.fontWeight}`,
    `line-height:${styles.lineHeight}`,
    `letter-spacing:${styles.letterSpacing}`,
    "word-break:break-word",
    "white-space:normal",
  ].join(";");
  document.body.appendChild(measure);
  return measure;
};

const appendViewMoreSuffix = (measure) => {
  measure.appendChild(document.createTextNode("..."));
  const link = document.createElement("span");
  link.style.cssText = [
    "display:inline",
    "margin:0",
    "padding:0",
    "border:none",
    "background:transparent",
    "font-family:inherit",
    "font-weight:700",
    "font-size:14px",
    "letter-spacing:0.1px",
    "line-height:inherit",
    "vertical-align:baseline",
    "white-space:nowrap",
  ].join(";");
  link.textContent = "View More";
  measure.appendChild(link);
};

const setMeasureContent = (measure, text, withViewMore) => {
  measure.textContent = "";
  measure.appendChild(document.createTextNode(text));
  if (withViewMore) {
    appendViewMoreSuffix(measure);
  }
};

const fitsWithinQuote = (measure, maxHeight) => {
  return measure.scrollHeight <= maxHeight + QUOTE_HEIGHT_TOLERANCE;
};

const getAvailableQuoteHeight = (textEl, containerEl) => {
  const card = textEl?.closest?.(".testimonial-card");
  const containerHeight = containerEl?.getBoundingClientRect().height || 0;

  if (!card) return containerHeight;

  const cardHeight = card.getBoundingClientRect().height;
  const styles = window.getComputedStyle(card);
  const paddingY =
    (parseFloat(styles.paddingTop) || 0) +
    (parseFloat(styles.paddingBottom) || 0);
  const gap = parseFloat(styles.rowGap || styles.gap) || 0;
  const divider = card.querySelector(".testimonial-card__divider");
  const author = card.querySelector(".testimonial-author");
  const dividerHeight = divider?.getBoundingClientRect().height || 0;
  const authorHeight = author?.getBoundingClientRect().height || 0;
  // quote + divider + author => 2 gaps
  const fromCard =
    cardHeight - paddingY - gap * 2 - dividerHeight - authorHeight;

  return Math.max(containerHeight, fromCard, 0);
};

const truncateQuoteForElement = (el, containerEl, fullText) => {
  const width =
    el?.getBoundingClientRect().width ||
    containerEl?.getBoundingClientRect().width ||
    0;
  if (!el || width < 1) {
    return { truncated: false, text: fullText };
  }

  const maxHeight = getAvailableQuoteHeight(el, containerEl);
  if (maxHeight < 1) {
    return { truncated: false, text: fullText };
  }

  const measure = createQuoteMeasure(el, width);

  setMeasureContent(measure, fullText, false);
  if (fitsWithinQuote(measure, maxHeight)) {
    document.body.removeChild(measure);
    return { truncated: false, text: fullText };
  }

  const words = fullText.trim().split(/\s+/).filter(Boolean);
  let low = 0;
  let high = words.length;
  let best = 0;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const candidate = words.slice(0, mid).join(" ");
    setMeasureContent(measure, candidate, true);

    if (fitsWithinQuote(measure, maxHeight)) {
      best = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  while (best < words.length) {
    const candidate = words.slice(0, best + 1).join(" ");
    setMeasureContent(measure, candidate, true);
    if (!fitsWithinQuote(measure, maxHeight)) break;
    best += 1;
  }

  document.body.removeChild(measure);

  if (best <= 0) {
    return { truncated: false, text: fullText };
  }

  return {
    truncated: true,
    text: words.slice(0, best).join(" "),
  };
};

const applyQuoteTruncation = () => {
  const flags = {};
  const texts = {};
  let changed = false;

  quoteRefs.value.forEach((el, index) => {
    if (!el) return;
    const fullText = testimonials[index]?.quote || "";
    const result = truncateQuoteForElement(
      el,
      quoteContainerRefs.value[index],
      fullText
    );
    flags[index] = result.truncated;
    texts[index] = result.text;

    if (
      truncatedFlags.value[index] !== result.truncated ||
      truncatedTexts.value[index] !== result.text
    ) {
      changed = true;
    }
  });

  if (changed) {
    truncatedFlags.value = flags;
    truncatedTexts.value = texts;
  }

  return changed;
};

const checkQuoteTruncation = () => {
  if (truncationFrame) {
    cancelAnimationFrame(truncationFrame);
  }

  truncationFrame = requestAnimationFrame(() => {
    truncationFrame = null;
    applyQuoteTruncation();

    // Second pass after layout settles with updated text
    nextTick(() => {
      requestAnimationFrame(() => {
        applyQuoteTruncation();
      });
    });
  });
};

const handleResize = () => {
  windowWidth.value = window.innerWidth;
  checkQuoteTruncation();
};

const currentItemsPerSlide = computed(() => {
  if (windowWidth.value < 768) {
    return 1;
  }

  if (windowWidth.value < 1200) {
    return Math.min(2, props.itemsPerSlide);
  }

  return props.itemsPerSlide;
});

const activeTestimonialIndex = ref(0);
const testimonialAutoPlayTimer = ref(null);
const touchStartX = ref(0);
const touchEndX = ref(0);
const isTransitioning = ref(false);

const maxSlideIndex = computed(() => {
  return Math.max(0, testimonials.length - currentItemsPerSlide.value);
});

const dotSlides = computed(() => {
  return testimonials.slice(0, maxSlideIndex.value + 1);
});

const viewportStyle = computed(() => ({
  "--items-per-slide": currentItemsPerSlide.value,
  "--total-slides": testimonials.length,
}));

const trackStyle = computed(() => ({
  transform: `translateX(-${
    activeTestimonialIndex.value * (100 / testimonials.length)
  }%)`,
}));

const openTestimonialModal = (item) => {
  selectedTestimonial.value = item;
  isModalOpen.value = true;
  stopAutoPlay();
};

const closeTestimonialModal = () => {
  isModalOpen.value = false;
  startAutoPlay();
};

watch(isModalOpen, (open) => {
  if (open) {
    stopAutoPlay();
  } else if (!open) {
    startAutoPlay();
  }
});

watch(currentItemsPerSlide, () => {
  if (activeTestimonialIndex.value > maxSlideIndex.value) {
    activeTestimonialIndex.value = maxSlideIndex.value;
  }
  nextTick(() => checkQuoteTruncation());
});

watch(activeTestimonialIndex, () => {
  nextTick(() => checkQuoteTruncation());
});

const goToTestimonial = (index) => {
  if (isTransitioning.value) return;
  isTransitioning.value = true;
  activeTestimonialIndex.value = Math.max(
    0,
    Math.min(index, maxSlideIndex.value)
  );
  resetAutoPlay();
  setTimeout(() => {
    isTransitioning.value = false;
  }, 500);
};

const nextTestimonial = () => {
  const next =
    activeTestimonialIndex.value >= maxSlideIndex.value
      ? 0
      : activeTestimonialIndex.value + 1;
  goToTestimonial(next);
};

const prevTestimonial = () => {
  const prev =
    activeTestimonialIndex.value <= 0
      ? maxSlideIndex.value
      : activeTestimonialIndex.value - 1;
  goToTestimonial(prev);
};

const startAutoPlay = () => {
  stopAutoPlay();
  if (isModalOpen.value) return;
  if (maxSlideIndex.value > 0) {
    testimonialAutoPlayTimer.value = setInterval(() => {
      const next =
        activeTestimonialIndex.value >= maxSlideIndex.value
          ? 0
          : activeTestimonialIndex.value + 1;
      activeTestimonialIndex.value = next;
    }, 6000);
  }
};

const stopAutoPlay = () => {
  if (testimonialAutoPlayTimer.value) {
    clearInterval(testimonialAutoPlayTimer.value);
    testimonialAutoPlayTimer.value = null;
  }
};

const resetAutoPlay = () => {
  stopAutoPlay();
  startAutoPlay();
};

const onTouchStart = (e) => {
  touchStartX.value = e.changedTouches[0].screenX;
};

const onTouchEnd = (e) => {
  touchEndX.value = e.changedTouches[0].screenX;
  const diff = touchStartX.value - touchEndX.value;
  if (Math.abs(diff) > 50) {
    if (diff > 0) {
      nextTestimonial();
    } else {
      prevTestimonial();
    }
  }
};

const onCarouselKeydown = (e) => {
  if (e.key === "ArrowLeft") {
    e.preventDefault();
    prevTestimonial();
  } else if (e.key === "ArrowRight") {
    e.preventDefault();
    nextTestimonial();
  }
};

onMounted(async () => {
  window.addEventListener("resize", handleResize);
  handleResize();
  startAutoPlay();

  await nextTick();
  checkQuoteTruncation();

  if (document.fonts?.ready) {
    document.fonts.ready.then(() => {
      checkQuoteTruncation();
    });
  }

  if (typeof ResizeObserver !== "undefined") {
    resizeObserver = new ResizeObserver(() => {
      checkQuoteTruncation();
    });

    slideRefs.value.forEach((slide) => {
      if (slide) {
        resizeObserver.observe(slide);
      }
    });

    quoteContainerRefs.value.forEach((container) => {
      if (container) {
        resizeObserver.observe(container);
      }
    });
  }
});

onUnmounted(() => {
  stopAutoPlay();
  window.removeEventListener("resize", handleResize);
  if (truncationFrame) {
    cancelAnimationFrame(truncationFrame);
  }
  resizeObserver?.disconnect();
});
</script>

<style scoped>
.testimonials-carousel-wrapper {
  width: 100%;
  font-family: var(--font-family-primary);
}

.testimonials-carousel {
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  outline: none;
}

.testimonials-carousel:focus-visible {
  outline: 2px solid var(--primary-pink);
  outline-offset: 4px;
  border-radius: 8px;
}

.carousel-viewport {
  overflow: hidden;
  flex: 1;
  border-radius: 24px;
}

.carousel-track {
  display: flex;
  align-items: stretch;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
  width: calc(var(--total-slides) * 100% / var(--items-per-slide, 2));
}

.carousel-slide {
  flex: 0 0 calc(100% / var(--total-slides, 4));
  min-width: 0;
  padding: 0 12px;
  box-sizing: border-box;
  display: flex;
  align-items: stretch;
}

.testimonial-card {
  flex: 1;
  width: 100%;
  background-color: var(--white-hover-1);
  border-radius: 24px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-sizing: border-box;
  position: relative;
  overflow: visible;
  height: 100%;
  min-height: 320px;
}

.testimonial-quote {
  flex: 1 1 0;
  min-height: 0;
  margin: 0;
  padding: 0;
  border: none;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.testimonial-quote__text {
  font-family: inherit;
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-headline-small);
  line-height: var(--line-height-headline-small);
  color: var(--black-body-text);
  margin: 0;
  word-break: break-word;
  width: 100%;
}

.testimonial-quote__view-more {
  display: inline;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  font-family: inherit;
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-title-small);
  line-height: inherit;
  letter-spacing: 0.1px;
  color: var(--primary-blue);
  cursor: pointer;
  vertical-align: baseline;
  white-space: nowrap;
}

.testimonial-quote__view-more:hover {
  text-decoration: underline;
}

.testimonial-quote__view-more:focus-visible {
  outline: 2px solid var(--primary-pink);
  outline-offset: 2px;
  border-radius: 4px;
}

.testimonial-card__divider,
.testimonial-modal__divider {
  width: 100%;
  height: 0;
  border: none;
  border-top: 1px solid var(--neutral-300);
  flex-shrink: 0;
}

.testimonial-author {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-shrink: 0;
  position: relative;
  width: 100%;
  min-height: 72px;
}

.testimonial-author__avatar-wrap {
  position: relative;
  flex-shrink: 0;
  width: 72px;
  height: 72px;
}

.testimonial-author__avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  background: var(--white);
}

.testimonial-author__sparkle {
  position: absolute;
  top: -10px;
  left: -8px;
  width: 28px;
  height: 28px;
  z-index: 1;
  pointer-events: none;
}

.testimonial-author__photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  display: block;
}

.testimonial-author__avatar--logo {
  padding: 6px;
  box-sizing: border-box;
}

.testimonial-author__avatar--logo .testimonial-author__photo {
  object-fit: contain;
}

.testimonial-author__avatar--initials {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(224, 0, 105, 0.1);
}

.testimonial-author__initials {
  font-family: inherit;
  font-weight: 600;
  font-size: var(--font-size-title-medium);
  line-height: 1;
  color: var(--primary-pink);
  letter-spacing: 0.02em;
}

.testimonial-author__info {
  min-width: 0;
  flex: 1;
}

.testimonial-author__name {
  display: block;
  font-family: inherit;
  font-style: normal;
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-title-large);
  line-height: var(--line-height-title-large);
  color: var(--pink-dark-1);
  margin: 0;
  word-break: break-word;
}

.testimonial-author__title {
  font-family: inherit;
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-body-medium);
  line-height: var(--line-height-body-medium);
  letter-spacing: 0.25px;
  color: var(--neutral-700);
  margin: 0;
  word-break: break-word;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
}

.carousel-arrow {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid rgba(0, 61, 106, 0.15);
  background: white;
  color: #003d6a;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.carousel-arrow:hover:not(:disabled) {
  background: var(--primary-pink);
  border-color: var(--primary-pink);
  color: white;
  box-shadow: 0 4px 16px rgba(224, 0, 105, 0.25);
  transform: scale(1.05);
}

.carousel-arrow:focus-visible {
  outline: 2px solid var(--primary-pink);
  outline-offset: 3px;
}

.carousel-arrow:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.carousel-dots {
  display: flex;
  justify-content: center;
  gap: 10px;
  padding: 24px 0 8px;
}

.carousel-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid rgba(0, 61, 106, 0.2);
  background: transparent;
  cursor: pointer;
  padding: 0;
  transition: all 0.3s ease;
}

.carousel-dot:hover {
  border-color: var(--primary-pink);
  background: rgba(224, 0, 105, 0.15);
}

.carousel-dot.active {
  background: var(--primary-pink);
  border-color: var(--primary-pink);
  transform: scale(1.2);
}

.carousel-dot:focus-visible {
  outline: 2px solid var(--primary-pink);
  outline-offset: 3px;
}

.testimonial-modal {
  background-color: var(--badge-blue-light-1);
  border-radius: 24px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  width: 100%;
  box-sizing: border-box;
}

.testimonial-modal__header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
}

.testimonial-modal__close {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}

.testimonial-modal__close:hover {
  background: rgba(59, 70, 72, 0.08);
}

.testimonial-modal__close:focus-visible {
  outline: 2px solid var(--primary-pink);
  outline-offset: 2px;
}

.testimonial-modal__close img {
  width: 40px;
  height: 40px;
  display: block;
}

.testimonial-modal__body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;
  width: 100%;
  padding: 12px 0;
  box-sizing: border-box;
  position: relative;
}

.testimonial-modal__sparkle {
  position: absolute;
  left: 0;
  top: 42%;
  width: 32px;
  height: 32px;
  opacity: 0.9;
  pointer-events: none;
}

.testimonial-modal__quote {
  margin: 0;
  width: 100%;
  text-align: center;
  font-family: inherit;
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-display-small);
  line-height: var(--line-height-display-small);
  color: var(--primary-blue);
  word-break: break-word;
}

.testimonial-author--modal {
  width: 100%;
}

@media (max-width: 1200px) {
  .carousel-arrow {
    width: 40px;
    height: 40px;
  }

  .carousel-arrow svg {
    width: 20px;
    height: 20px;
  }
}

@media (max-width: 768px) {
  .testimonials-carousel {
    gap: 0;
  }

  .carousel-arrow {
    display: none;
  }

  .carousel-slide {
    padding: 0;
  }

  .testimonial-card {
    padding: 24px;
    gap: 16px;
    min-height: 300px;
  }

  .testimonial-quote__text {
    font-size: var(--font-size-headline-small);
    line-height: var(--line-height-headline-small);
  }

  .testimonial-author__avatar-wrap {
    width: 72px;
    height: 72px;
  }

  .testimonial-author__sparkle {
    width: 28px;
    height: 28px;
    top: -10px;
    left: -8px;
  }

  .testimonial-author__name {
    font-size: var(--font-size-title-big);
    line-height: var(--line-height-title-big);
    letter-spacing: 0.15px;
  }

  .carousel-dots {
    padding: 16px 0 4px;
  }

  .carousel-dot {
    width: 10px;
    height: 10px;
  }

  .testimonial-modal {
    padding: 16px;
  }

  .testimonial-modal__body {
    gap: 24px;
    padding: 8px 0;
  }

  .testimonial-modal__sparkle {
    display: none;
  }

  .testimonial-modal__quote {
    font-size: var(--font-size-headline-small);
    line-height: var(--line-height-headline-small);
  }

  .testimonial-modal__close,
  .testimonial-modal__close img {
    width: 32px;
    height: 32px;
  }
}

@media (max-width: 480px) {
  .carousel-dots {
    padding: 12px 0 4px;
    gap: 8px;
  }

  .carousel-dot {
    width: 8px;
    height: 8px;
  }
}
</style>

<style>
.testimonial-modal-dialog {
  margin: 16px !important;
  box-shadow: none !important;
}

.testimonial-modal-dialog > .v-overlay__content,
.testimonial-modal-dialog.v-overlay__content {
  box-shadow: none !important;
}
</style>
